import { MentionMembersContent } from "@/components/mention-members-content";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Mention, MentionTextarea } from "@/components/ui/mention";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { useAttachmentsByRoomQuery } from "@/features/attachments/api/attachments-hooks";
import type { AttachmentsByMessage } from "@/features/attachments/attachments.types";
import { FileIconHelper } from "@/features/attachments/components/file-icon-helper";
import { useContainerMembersQuery } from "@/features/containers/api/containers-hooks";
import { createMentionNotification } from "@/features/notifications/api/notifications-api";
import { getDisplayName } from "@/features/profiles/profile.utils";
import { useSkillsQuery } from "@/features/skills/api/skill-hooks";
import type { Skill } from "@/features/skills/api/skills-api";
import { useAutoGrowTextarea } from "@/hooks/use-auto-grow-textarea";
import { useScrollTo } from "@/hooks/use-scroll-to";
import { isMentionPickPending } from "@/lib/mention-menu";
import { cn } from "@/lib/utils";
import type { AiMember, ChatMessageWithAuthorView, Profile } from "@shared/types/supabase.types";
import {
  AlertTriangle,
  Check,
  Globe,
  Paperclip,
  PlusIcon,
  Reply,
  RotateCw,
  Send,
  Square,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CreditWarningMetaEvent } from "../api/chat-ai";
import { isHandleMentioned, mentionsHuman } from "../chat-ai-routing";
import {
  formatDateSeparator,
  formatFileSize,
  isAskUserAnswered,
  parseAskUserBlock,
  resolveMentionMembersContainerId,
  resolveSingleTargetedPersonaId,
} from "../chat.utils";
import { insertNewlineAtCaret, isChatSendKey, isNewlineShortcut } from "../composer-keys";
import { useDictationLanguage } from "../hooks/use-dictation-language";
import type { FailedAiTurn } from "../hooks/use-realtime-chat";
import { useSlashCommand } from "../hooks/use-slash-command";
import { type VoiceDictationError, useVoiceDictation } from "../hooks/use-voice-dictation";
import { appendDictatedText } from "../voice-dictation";
import { buildSpokenMentionCandidates, resolveSpokenMentions } from "../voice-mentions";
import { ChatMessageItem } from "./chat-message";
import { CreditsExhaustedTurn } from "./credits-exhausted-turn";
import { ProviderErrorTurn } from "./provider-error-turn";
import { SlashCommandContent } from "./slash-command-content";
import {
  VoiceDictationButton,
  VoiceDictationLanguagePicker,
  VoiceDictationStrip,
} from "./voice-dictation-control";

const EMPTY_ATTACHMENTS: AttachmentsByMessage = {};
const EMPTY_SKILLS: Skill[] = [];
const FILE_INPUT_ACCEPT =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.tsv,.md,.json,.rtf,.odt,.epub,.xml,.fb2,.ipynb,.bib,.tex";
const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

function formatCreditSummary(balance: number, unit: string): string {
  return `${balance.toLocaleString()} ${unit}`;
}

interface RealtimeChatProps {
  roomId: string;
  roomName: string;
  projectContainerId: string;
  /**
   * CR-107: whether the room is shared with the whole project
   * (`containers.is_shared`). A shared room has no `container_members` rows for
   * its audience, so the humans who can be @mentioned — in the picker AND as
   * notification recipients — are the parent project's members.
   */
  isShared: boolean;
  allMessages: ChatMessageWithAuthorView[];
  aiMembers?: AiMember[];
  /**
   * The room's kind (CR-25), mirroring useRealtimeChat: "room" = AI Chat,
   * "message_room" = Message room. Required on purpose: the reply chip is
   * gated on it and would otherwise fail OPEN to AI-chat behaviour in a
   * message room, violating the CR-50 explicit-mention invariant.
   */
  roomContainerType: "room" | "message_room";
  /**
   * The AI Chat's default persona — the one that replies when nothing is
   * @mentioned. Resolved by the page from the same member list the send path
   * routes against, so the chip and the routing agree. In an AI Chat the reply
   * chip stays off for this persona: an un-mentioned message already reaches it.
   */
  defaultAiPersonaId?: string | null;
  profile: Profile;
  scrollToMessageId?: string;
  initialMessage?: string;
  onSendMessage: (payload: {
    content: string;
    attachments: File[];
    webSearchEnabled: boolean;
    messageId?: string;
  }) => Promise<boolean>;
  onSendAskUserAnswer: (
    answer: string,
    aiPersona: AiMember,
    webSearchEnabled: boolean,
    skipAiTurn?: boolean,
  ) => Promise<void>;
  onResetSendError: () => void;
  isSending: boolean;
  /** True while an AI turn is streaming — disables Retry to prevent a double-fire. */
  isAiResponding?: boolean;
  /** CR-172: an AI reply to the user's own message is in progress — Stop replaces Send. */
  isAiTurnActive?: boolean;
  /** CR-172: the Stop was sent and the replies are settling — Stop shows a spinner. */
  isStoppingAiTurn?: boolean;
  /** CR-172: stop every AI reply the user has in progress in this room. */
  onStopAiTurns?: () => void;
  sendError: Error | null;
  creditWarning: CreditWarningMetaEvent | null;
  failedAiTurns?: FailedAiTurn[];
  onRetryAiTurn?: (failedTurnId: string) => Promise<void> | void;
  /** CR-158: slug of the room's workspace, for the "no credits left" card's Subscription link. */
  workspaceSlug?: string;
  /** CR-158: true for workspace owners/admins — they get the Subscription link. */
  canManageCredits?: boolean;
}

/**
 * Realtime chat component
 */
export const RealtimeChat = ({
  roomId,
  projectContainerId,
  isShared,
  allMessages,
  aiMembers,
  roomContainerType,
  defaultAiPersonaId,
  profile,
  scrollToMessageId,
  initialMessage,
  onSendMessage,
  onSendAskUserAnswer,
  onResetSendError,
  isSending,
  isAiResponding,
  isAiTurnActive = false,
  isStoppingAiTurn = false,
  onStopAiTurns,
  sendError,
  creditWarning,
  failedAiTurns,
  onRetryAiTurn,
  workspaceSlug,
  canManageCredits = false,
}: RealtimeChatProps) => {
  const [newMessage, setNewMessage] = useState(initialMessage ?? "");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [inputResetKey, setInputResetKey] = useState(0);
  // Remounted with a new key after every send; only the first mount shows a ?draft= seed.
  const isFirstComposerMount = inputResetKey === 0;
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mentionInputRef = useRef<HTMLTextAreaElement>(null);
  // CR-171: two rows when empty, growing with the draft (no cap, Q3).
  useAutoGrowTextarea(mentionInputRef, newMessage);
  // CR-171: a send remounts the composer and disables it while isSending (react-query
  // flips isPending a tick later, disabling the freshly focused textarea): either way
  // focus ends on <body>. Hand it back once the send is over — unless the user has
  // moved focus somewhere else meanwhile.
  const refocusAfterSendRef = useRef(false);
  useEffect(() => {
    if (isSending || !refocusAfterSendRef.current) {
      return;
    }
    refocusAfterSendRef.current = false;
    const active = document.activeElement;
    // A text selection in the message list also leaves focus on <body>: keep it.
    const hasSelection = window.getSelection()?.isCollapsed === false;
    if ((!active || active === document.body) && !hasSelection) {
      mentionInputRef.current?.focus();
    }
  }, [isSending]);

  // A ?draft= that changes while this room stays mounted (RealtimeChat is keyed on
  // roomId): write it into the uncontrolled textarea and the draft state. The first
  // mount is seeded by useState above and by the composer's defaultValue.
  useEffect(() => {
    if (initialMessage && mentionInputRef.current) {
      mentionInputRef.current.value = initialMessage;
      setNewMessage(initialMessage);
    }
  }, [initialMessage]);
  const mentionedValuesRef = useRef<string[]>([]);

  // CR-107: one source for both mention surfaces — the picker's rows and the
  // display names the send path resolves into notification recipients. They
  // must agree, or a listed teammate could be mentioned without being notified.
  const mentionMembersContainerId = resolveMentionMembersContainerId({
    roomId,
    projectContainerId,
    isShared,
  });
  const { data: mentionMembersData } = useContainerMembersQuery(mentionMembersContainerId, [
    "user",
  ]);
  const membersByDisplayName = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of mentionMembersData ?? []) {
      const name = getDisplayName({
        firstName: m.profile_first_name,
        lastName: m.profile_last_name,
        email: m.profile_email,
      });
      map.set(name, m.user_id);
    }
    return map;
  }, [mentionMembersData]);

  // When the draft targets exactly one persona, scope the slash catalog to that
  // persona's allowlist so we don't offer commands the server would drop. Zero
  // or multiple @-mentions fall back to the full catalog.
  const targetedPersonaId = useMemo(
    () => resolveSingleTargetedPersonaId(aiMembers, newMessage),
    [aiMembers, newMessage],
  );
  const { data: allSkillsData } = useSkillsQuery(projectContainerId, targetedPersonaId);
  const allSkills = allSkillsData ?? EMPTY_SKILLS;
  const {
    open: slashOpen,
    filteredSkills,
    selectedIndex: slashSelectedIndex,
    handleSelect: handleSkillSelect,
    handleInputChange: handleSlashInputChange,
    handleKeyDown: handleSlashKeyDown,
    dismiss: dismissSlash,
  } = useSlashCommand({
    inputValue: newMessage,
    allSkills,
    inputRef: mentionInputRef,
    onInputValueChange: setNewMessage,
  });

  const sortedMessages = useMemo(() => {
    return [...allMessages].sort((a, b) => a.created_at!.localeCompare(b.created_at!));
  }, [allMessages]);

  // The persona that spoke last, when the thread ends with an AI reply to a
  // message THIS user sent. In a group room everyone sees everyone's AI
  // exchanges, so without the authorship check a bystander's client would arm
  // the chip off someone else's conversation. Consecutive AI turns (one message
  // that @mentioned two personas) are skipped over to find who prompted them.
  const lastAiReply = useMemo(() => {
    const last = sortedMessages.at(-1);
    if (!last?.id || last.actor_type !== "ai" || !last.ai_persona_id) return null;

    let prompter: ChatMessageWithAuthorView | undefined;
    for (let index = sortedMessages.length - 1; index >= 0; index--) {
      if (sortedMessages[index].actor_type !== "ai") {
        prompter = sortedMessages[index];
        break;
      }
    }
    if (prompter?.user_id !== profile.user_id) return null;

    const handle = aiMembers?.find(
      (m) => m.ai_persona_id === last.ai_persona_id,
    )?.ai_persona_handle;
    if (!handle) return null;
    return { messageId: last.id, personaId: last.ai_persona_id, handle };
  }, [sortedMessages, aiMembers, profile.user_id]);

  const pendingQuestionPersona = useMemo((): AiMember | null => {
    const last = sortedMessages.at(-1);
    if (!last || last.actor_type !== "ai" || !last.content || !last.ai_persona_id) return null;
    const lastIndex = sortedMessages.length - 1;
    if (parseAskUserBlock(last.content) === null || isAskUserAnswered(lastIndex, sortedMessages))
      return null;
    return aiMembers?.find((m) => m.ai_persona_id === last.ai_persona_id) ?? null;
  }, [sortedMessages, aiMembers]);

  // CR-49: the reply chip replaced the auto-mention text prefill. The prefill
  // wrote `@handle ` into the input the moment an AI reply started streaming,
  // racing whatever the user was typing and producing "@kairo @kairo". The chip
  // never writes to the input and never grabs focus off an AI reply — it only
  // prepends the handle to the OUTGOING content at send time, so duplication is
  // impossible. (Dismissing does return focus to the input, but only because
  // the user's own click is what removed the focused button.)
  const [dismissedReplyMessageId, setDismissedReplyMessageId] = useState<string | null>(null);

  // A handle the user typed already routes the message on its own; prepending
  // would add a second mention, so the chip steps aside while one is present.
  const mentionsPersona = useCallback(
    (text: string) => (aiMembers ?? []).some((m) => isHandleMentioned(text, m.ai_persona_handle)),
    [aiMembers],
  );
  const hasTypedAiMention = useMemo(
    () => mentionsPersona(newMessage),
    [mentionsPersona, newMessage],
  );

  // CR-220: a message that @mentions a teammate is left to the server, which
  // stays silent when it is addressed to that teammate. Prepending the chip's
  // handle would make it an explicit persona mention, which always gets an
  // answer — so the chip steps aside too. The sender is excluded, as on the server.
  // Known gap: in a shared room the server also counts the room's own members,
  // this list (the CR-107 picker's) only the project's — a room-only guest typed
  // by hand keeps the chip, i.e. the persona answers (the pre-CR-220 behaviour).
  // From the rows, not membersByDisplayName: a teammate who shares the
  // sender's display name must still count.
  const teammateRows = useMemo(
    () => (mentionMembersData ?? []).filter((m) => m.user_id !== profile.user_id),
    [mentionMembersData, profile.user_id],
  );
  const teammateNames = useMemo(
    () =>
      teammateRows.map((m) =>
        getDisplayName({
          firstName: m.profile_first_name,
          lastName: m.profile_last_name,
          email: m.profile_email,
        }),
      ),
    [teammateRows],
  );
  const hasTypedHumanMention = useMemo(
    () => mentionsHuman(newMessage, teammateNames),
    [newMessage, teammateNames],
  );

  // On top of the authorship gate in lastAiReply: AI Chats route un-mentioned
  // messages to the default persona already, so the chip only earns its place
  // there when a NON-default persona answered. Message rooms get NO chip at
  // all (CR-50, reversing CR-49's default-on): messages are strictly
  // human-first, and every AI invocation there must be an explicit @mention.
  const isAiChat = roomContainerType !== "message_room";
  const replyChip =
    isAiChat &&
    lastAiReply &&
    !hasTypedAiMention &&
    !hasTypedHumanMention &&
    dismissedReplyMessageId !== lastAiReply.messageId &&
    lastAiReply.personaId !== defaultAiPersonaId
      ? lastAiReply
      : null;

  const messageIds = useMemo(
    () =>
      sortedMessages
        .map((message) => message.id)
        .filter((messageId): messageId is string => !!messageId),
    [sortedMessages],
  );

  const { data, isPending: isAttachmentsByMessagePending } = useAttachmentsByRoomQuery(
    roomId,
    messageIds,
  );
  const attachmentsByMessage = data ?? EMPTY_ATTACHMENTS;

  const hasLoadedAttachmentsOnce = useRef(false);

  if (Object.keys(attachmentsByMessage).length > 0) {
    hasLoadedAttachmentsOnce.current = true;
  }

  const shouldBlockInitialRender =
    sortedMessages.length > 0 && !hasLoadedAttachmentsOnce.current && isAttachmentsByMessagePending;

  useEffect(() => {
    if (!sendError) return;
    const timer = setTimeout(onResetSendError, 5000);
    return () => clearTimeout(timer);
  }, [sendError, onResetSendError]);

  // CR-171: shouldBlockInitialRender is in both effects' deps — the list first renders
  // behind the attachments spinner, and neither target exists until it goes away. The
  // bottom jump is declared first (effects run in declaration order) so a linked
  // message (CR-150) scrolls into view after it.
  const bottomRef = useScrollTo<HTMLDivElement>([
    sortedMessages.length,
    webSearchEnabled,
    shouldBlockInitialRender,
  ]);

  const lastScrolledMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!scrollToMessageId || lastScrolledMessageIdRef.current === scrollToMessageId) return;
    const el = document.querySelector(`[data-message-id="${CSS.escape(scrollToMessageId)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    lastScrolledMessageIdRef.current = scrollToMessageId;
  }, [scrollToMessageId, sortedMessages, shouldBlockInitialRender]);

  const isMessageEmpty = !newMessage.trim() && attachments.length === 0;

  const handleAttachmentSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files ?? []);
      if (!selectedFiles.length) return;

      if (sendError) {
        onResetSendError();
      }

      const oversizedFiles = selectedFiles.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        setAttachmentError(
          `${oversizedFiles.map((f) => f.name).join(", ")} ${oversizedFiles.length === 1 ? "exceeds" : "exceed"} the 25 MB limit.`,
        );
        event.target.value = "";
        return;
      }

      setAttachments((currentAttachments) => {
        const combined = [...currentAttachments, ...selectedFiles];
        if (combined.length > MAX_ATTACHMENTS) {
          setAttachmentError(`You can attach at most ${MAX_ATTACHMENTS} files per message.`);
          return currentAttachments;
        }
        setAttachmentError(null);
        return combined;
      });
      event.target.value = "";
    },
    [onResetSendError, sendError],
  );

  const handleRemoveAttachment = useCallback((indexToRemove: number) => {
    setAttachments((currentAttachments) => {
      const next = currentAttachments.filter((_, index) => index !== indexToRemove);
      if (next.length < MAX_ATTACHMENTS) setAttachmentError(null);
      return next;
    });
  }, []);

  const handleOpenFilePicker = useCallback(() => {
    if (creditWarning?.status === "blocked") {
      return;
    }
    fileInputRef.current?.click();
  }, [creditWarning?.status]);

  // CR-202: voice dictation. The live transcript is written into the textarea
  // after the draft typed before the mic was tapped. The textarea is
  // uncontrolled (diceui): mutate it first, then sync state — never through
  // handleSlashInputChange, or a "/" in the transcript would open the slash menu.
  const [dictationError, setDictationError] = useState<VoiceDictationError | null>(null);
  const draftBeforeDictationRef = useRef("");
  const dictationLanguage = useDictationLanguage(profile.user_id);

  // CR-215: "at Alice" / "arobase Alice" / "at kairo" in the transcript becomes
  // the same @First Last / @handle text the @ picker inserts. Built from the
  // CR-107 picker rows (minus the sender) so a spoken name and a picked one
  // reach the same people. Only the transcript is converted, never the draft
  // typed before the mic was tapped.
  const spokenMentionCandidates = useMemo(
    () =>
      buildSpokenMentionCandidates({
        humans: teammateRows.map((m) => ({
          userId: m.user_id,
          firstName: m.profile_first_name,
          lastName: m.profile_last_name,
          email: m.profile_email,
        })),
        personas: (aiMembers ?? []).flatMap((m) =>
          m.ai_persona_id
            ? [
                {
                  personaId: m.ai_persona_id,
                  handle: m.ai_persona_handle,
                  displayName: m.ai_persona_display_name,
                },
              ]
            : [],
        ),
      }),
    [aiMembers, teammateRows],
  );
  // Display names of the teammates dictated into the current draft. It only
  // grows until the send (a second dictation must keep the first one's
  // mention); the send keeps the names whose @First Last is still in the text,
  // so a stale name (an interim result, an abandoned draft) is dropped there.
  const dictatedMentionsRef = useRef<Set<string>>(new Set());
  const resolveDictated = (best: string) => {
    const resolved = resolveSpokenMentions(best, spokenMentionCandidates, dictationLanguage.choice);
    for (const name of resolved.humans) {
      dictatedMentionsRef.current.add(name);
    }
    return resolved.text;
  };
  const applyDictation = (best: string) => {
    const value = appendDictatedText(draftBeforeDictationRef.current, resolveDictated(best));
    const input = mentionInputRef.current;
    if (input) {
      input.value = value;
      // Moving the caret of an unfocused field can scroll it into view (iOS).
      if (document.activeElement === input) {
        input.setSelectionRange(value.length, value.length);
      }
    }
    setNewMessage(value);
  };
  const voice = useVoiceDictation({
    onTranscript: applyDictation,
    onEnd: applyDictation,
    onError: setDictationError,
    lang: dictationLanguage.lang,
  });
  const isComposerBlocked = isSending || creditWarning?.status === "blocked";
  const isDictating = voice.status !== "idle";

  // Credits can run out mid-dictation: stop listening (the text is kept).
  useEffect(() => {
    if (isComposerBlocked && voice.status === "listening") {
      void voice.stop();
    }
  }, [isComposerBlocked, voice.status, voice.stop]);

  const handleStartDictation = () => {
    if (isComposerBlocked) {
      return;
    }
    dismissSlash();
    setDictationError(null);
    if (sendError) {
      onResetSendError();
    }
    draftBeforeDictationRef.current = mentionInputRef.current?.value ?? newMessage;
    voice.start();
  };

  // CR-171: the one send path — the form's submit (send button) and Enter in
  // the composer both run it. CR-202: "Send now" passes the dictated text as
  // contentOverride, since the newMessage state still holds the pre-stop text.
  const submitMessage = useCallback(
    async (options?: { contentOverride?: string }) => {
      const trimmedMessage = (options?.contentOverride ?? newMessage).trim();
      if (
        (!trimmedMessage && attachments.length === 0) ||
        isSending ||
        creditWarning?.status === "blocked"
      ) {
        if (creditWarning?.status === "blocked") {
          window.alert("AI usage is temporarily disabled until workspace credits are replenished.");
        }
        return;
      }

      // A late recognition result must not write into the reset composer.
      voice.cancel();

      if (sendError) {
        onResetSendError();
      }

      // The chip's handle is prepended to the outgoing content only — never to
      // the input — so the AI routing that parses mentions out of the sent
      // content still engages the persona being replied to (CR-49).
      // CR-215: decided on the text being sent, not on the render's draft — the
      // last words of a Send now dictation (possibly "at aria" or "at Alice")
      // arrive after this callback's render.
      const chip =
        replyChip &&
        !mentionsPersona(trimmedMessage) &&
        !mentionsHuman(trimmedMessage, teammateNames)
          ? replyChip
          : null;
      const outgoingContent = chip ? `${chip.handle} ${trimmedMessage}`.trim() : trimmedMessage;

      // CR-215: a dictated name counts only while its @First Last is still in
      // the text (deleted by hand → no notification). Picked names keep the
      // picker's own bookkeeping: diceui already drops a value whose token was
      // deleted. Like the CR-220 gate, a mention inside a > quote or code does
      // not count.
      const dictatedMentions = [...dictatedMentionsRef.current].filter((name) =>
        mentionsHuman(trimmedMessage, [name]),
      );
      const mentionedAtSend = [...mentionedValuesRef.current, ...dictatedMentions];
      mentionedValuesRef.current = [];
      dictatedMentionsRef.current = new Set();
      const messageId = crypto.randomUUID();

      // Reset input regardless of successful send
      refocusAfterSendRef.current = true;
      setNewMessage("");
      setAttachments([]);
      setAttachmentError(null);
      setInputResetKey((key) => key + 1);

      if (pendingQuestionPersona) {
        await onSendAskUserAnswer(
          "<!-- ask-user-answer -->\n*(skipped — user continued with a new message)*",
          pendingQuestionPersona,
          webSearchEnabled,
          true,
        );
      }

      const sent = await onSendMessage({
        content: outgoingContent,
        attachments,
        webSearchEnabled,
        messageId,
      });

      if (sent && mentionedAtSend.length > 0) {
        const seen = new Set<string>();
        for (const displayName of mentionedAtSend) {
          const userId = membersByDisplayName.get(displayName);
          if (!userId || userId === profile.user_id || seen.has(userId)) continue;
          seen.add(userId);
          void createMentionNotification({
            recipientId: userId,
            type: "chat_mention",
            containerId: roomId,
            sourceId: messageId,
            // A multi-line draft reads as one line in the bell and the email.
            body: trimmedMessage.replace(/\s+/g, " ").slice(0, 200),
          });
        }
      }

      if (sendError) {
        console.error("Failed to send message:", sendError);
      }
    },
    [
      attachments,
      creditWarning?.status,
      isSending,
      membersByDisplayName,
      mentionsPersona,
      newMessage,
      onResetSendError,
      onSendAskUserAnswer,
      onSendMessage,
      pendingQuestionPersona,
      profile,
      replyChip,
      roomId,
      sendError,
      teammateNames,
      voice.cancel,
      webSearchEnabled,
    ],
  );

  // CR-202: stop() resolves "" when the dictation was cancelled (a send or an
  // unmount in the meantime) — nothing to send then.
  const handleSendDictation = async () => {
    const dictated = await voice.stop();
    if (!dictated.trim()) {
      return;
    }
    await submitMessage({
      contentOverride: appendDictatedText(
        draftBeforeDictationRef.current,
        resolveDictated(dictated),
      ),
    });
  };

  const handleSendMessage = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void submitMessage();
    },
    [submitMessage],
  );

  // CR-171: the slash menu handles its keys first (it default-prevents the Enter
  // that picks a skill); then Ctrl/Cmd/Alt+Enter inserts a line break and plain Enter
  // sends. Shift+Enter falls through to the textarea's native line break.
  const handleComposerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      handleSlashKeyDown(event);
      if (event.defaultPrevented) {
        return;
      }
      // diceui takes Enter only to pick a highlighted member; with its menu open
      // but nothing highlighted, Enter sends as it did from the old input.
      const mentionPickPending = isMentionPickPending(event.currentTarget);
      if (isNewlineShortcut(event, { mentionPickPending })) {
        event.preventDefault();
        insertNewlineAtCaret(event.currentTarget);
        return;
      }
      if (isChatSendKey(event, { mentionPickPending })) {
        // Never let Enter add a line break, even when there is nothing to send.
        event.preventDefault();
        void submitMessage();
      }
    },
    [handleSlashKeyDown, submitMessage],
  );

  if (shouldBlockInitialRender) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <Spinner className="text-muted-foreground size-6" />
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground flex h-full w-full flex-col overflow-y-auto px-6 antialiased">
      {creditWarning ? (
        <Alert
          variant={creditWarning.status === "blocked" ? "destructive" : "default"}
          className={cn(
            "mb-4",
            creditWarning.status === "sync"
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : undefined,
          )}
        >
          <AlertDescription className="flex flex-col gap-1 text-sm">
            <span>
              {creditWarning.status === "warning"
                ? "Workspace credits are running low. Add credits soon to avoid interruptions."
                : creditWarning.status === "sync"
                  ? "Credits are critically low. Usage is being settled after each request; responses may take longer."
                  : "AI usage is temporarily disabled until more credits are added to this workspace."}
            </span>
            <span className="text-muted-foreground text-xs">
              Remaining: {formatCreditSummary(creditWarning.balanceRemaining, creditWarning.unit)}
              {creditWarning.lastReconciledAt
                ? ` • Updated ${new Date(creditWarning.lastReconciledAt).toLocaleString()}`
                : ""}
            </span>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Messages */}
      <div className="flex-1 space-y-4 py-4">
        {sortedMessages.length === 0 && (
          <div className="text-muted-foreground text-center text-sm">
            No messages yet. Start the conversation!
          </div>
        )}

        <div className="space-y-1">
          {sortedMessages.map((message, index) => {
            if (message.content?.startsWith("<!-- ask-user-answer -->")) {
              return null;
            }

            const prevMessage = index > 0 ? sortedMessages[index - 1] : null;

            // Convert ISO string → Date (built-in)
            const messageDate = message.created_at ? new Date(message.created_at) : null;

            const prevDate =
              prevMessage?.created_at != null ? new Date(prevMessage.created_at) : null;

            // Compare days using built-in toDateString()
            const showDateSeparator =
              index === 0 ||
              (messageDate && prevDate && messageDate.toDateString() !== prevDate.toDateString());

            const showHeader = !prevMessage || prevMessage.actor_id !== message.actor_id;

            const aiPersona =
              message.actor_type === "ai" && message.ai_persona_id
                ? aiMembers?.find((m) => m.ai_persona_id === message.ai_persona_id)
                : undefined;

            return (
              <div key={message.id} data-message-id={message.id}>
                {!!showDateSeparator && !!messageDate && (
                  <div className="my-4 flex items-center gap-3">
                    <div className="bg-border h-px flex-1" />
                    <div className="text-muted-foreground rounded-full border px-3 py-1 text-xs">
                      {formatDateSeparator(messageDate)}
                    </div>
                    <div className="bg-border h-px flex-1" />
                  </div>
                )}

                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <ChatMessageItem
                    message={message}
                    isOwnMessage={message.user_id === profile.user_id}
                    showHeader={showHeader}
                    attachments={message.id ? (attachmentsByMessage[message.id] ?? []) : []}
                    messages={sortedMessages}
                    messageIndex={index}
                    onSubmitAskUserAnswer={
                      aiPersona
                        ? async (answer) => {
                            try {
                              await onSendAskUserAnswer(answer, aiPersona, webSearchEnabled);
                            } catch (err) {
                              // The human answer is stored independently; only
                              // the follow-up AI turn failed. Alert and stop —
                              // do NOT rethrow (the submit handler upstream has
                              // no catch, so a rethrow becomes an unhandled
                              // rejection reported to Sentry).
                              window.alert(
                                `Failed to submit answer: ${err instanceof Error ? err.message : "Unknown error"}`,
                              );
                            }
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
            );
          })}

          {/* Inline AI-failure state with retry (CR-25) — shown where the AI
              reply would have appeared, e.g. a failed @mention in a Message room. */}
          {(failedAiTurns ?? []).map((turn) =>
            turn.kind === "credits_exhausted" ? (
              // CR-158: no credits left — explained, never retried.
              <CreditsExhaustedTurn
                key={turn.id}
                personaName={turn.aiPersona.ai_persona_display_name}
                workspaceSlug={workspaceSlug}
                canManageCredits={canManageCredits}
              />
            ) : turn.kind === "provider_error" ? (
              <ProviderErrorTurn
                key={turn.id}
                personaName={turn.aiPersona.ai_persona_display_name}
                retryable={turn.retryable === true}
                onRetry={onRetryAiTurn ? () => void onRetryAiTurn(turn.id) : undefined}
                retryDisabled={isSending || isAiResponding}
              />
            ) : (
              <div
                key={turn.id}
                data-testid="failed-ai-turn"
                className="border-destructive/30 bg-destructive/5 my-2 flex items-start gap-3 rounded-lg border px-4 py-3"
              >
                <AlertTriangle className="text-destructive mt-0.5 size-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground text-sm font-medium">
                    {turn.aiPersona.ai_persona_display_name} failed to respond
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs break-words">{turn.message}</p>
                </div>
                {/* CR-141: a turn the server gave up on has no Retry — it already retried. */}
                {onRetryAiTurn && turn.retryable !== false ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    data-testid="failed-ai-turn-retry"
                    disabled={isSending || isAiResponding}
                    onClick={() => void onRetryAiTurn(turn.id)}
                  >
                    <RotateCw className="mr-1 size-3" />
                    Retry
                  </Button>
                ) : null}
              </div>
            ),
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Error display */}
      {!!sendError && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-2">
          <p className="text-sm text-red-600">
            Failed to send message:{" "}
            {sendError instanceof Error ? sendError.message : "Unknown error"}
          </p>
        </div>
      )}
      <form onSubmit={handleSendMessage} className="bg-background sticky bottom-0 w-full pt-1 pb-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept={FILE_INPUT_ACCEPT}
          onChange={handleAttachmentSelect}
          tabIndex={-1}
          disabled={isComposerBlocked}
        />

        <div className="border-border bg-background flex items-end gap-2 rounded-2xl border p-2 shadow-sm">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="cursor-pointer rounded-full text-left hover:opacity-75"
                disabled={isComposerBlocked}
                aria-label="Open attachment menu"
              >
                <Avatar
                  className="bg-muted text-muted-foreground flex items-center justify-center"
                  style={{ width: 36, height: 36 }}
                >
                  <PlusIcon style={{ width: 20, height: 20 }} />
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                mentionInputRef.current?.focus();
              }}
            >
              <DropdownMenuItem
                disabled={creditWarning?.status === "blocked"}
                onSelect={() => {
                  if (creditWarning?.status === "blocked") return;
                  setWebSearchEnabled(!webSearchEnabled);
                }}
              >
                <Globe className="size-4" />
                Web search
                {webSearchEnabled ? <Check className="ml-auto size-4" /> : null}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={handleOpenFilePicker}
                disabled={
                  attachments.length >= MAX_ATTACHMENTS || creditWarning?.status === "blocked"
                }
              >
                <Paperclip className="size-4" />
                Upload attachments
                {attachments.length >= MAX_ATTACHMENTS && (
                  <span className="text-muted-foreground ml-auto text-xs">
                    Max {MAX_ATTACHMENTS}
                  </span>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {webSearchEnabled ? (
              <div className="animate-in fade-in duration-200">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-blue-600/10 px-2.5 py-1 text-xs font-medium text-blue-600">
                  <Globe className="size-3" />
                  Web search
                  <button
                    type="button"
                    onClick={() => setWebSearchEnabled(false)}
                    className="focus-visible:ring-ring ml-0.5 rounded-full p-0.5 transition-colors hover:bg-blue-600/20 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    aria-label="Disable web search"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              </div>
            ) : null}
            {attachmentError ? <p className="text-destructive text-xs">{attachmentError}</p> : null}
            {attachments.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {attachments.map((attachment, index) => (
                  <div
                    key={`${attachment.name}-${attachment.lastModified}-${index}`}
                    className={cn(
                      "bg-muted hover:bg-muted/70 relative flex flex-row items-center gap-2 rounded-xl px-2 py-2 transition-colors",
                      "cursor-pointer",
                    )}
                    onClick={() => {
                      const url = URL.createObjectURL(attachment);

                      const a = document.createElement("a");
                      a.href = url;
                      a.target = "_blank";
                      a.rel = "noopener noreferrer";
                      a.download = attachment.name; // ← key line

                      document.body.appendChild(a);
                      a.click();
                      a.remove();

                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    }}
                  >
                    <FileIconHelper mimeType={attachment.type} />
                    <div className="flex min-w-0 flex-col">
                      <p className="truncate pr-5 text-xs font-medium">{attachment.name}</p>
                      <p className="text-muted-foreground text-xs">
                        {formatFileSize(attachment.size)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="text-muted-foreground hover:text-foreground absolute top-1 right-1 size-5 shrink-0 rounded-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveAttachment(index);
                      }}
                      aria-label={`Remove ${attachment.name}`}
                      disabled={isSending}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Permanently mounted: several screen readers ignore a live region
                that appears together with its first content. The visible chip
                carries no role so its dismiss button isn't announced twice. */}
            <div className="sr-only" role="status">
              {replyChip ? <span>Will mention {replyChip.handle}</span> : null}
            </div>

            {voice.isSupported ? (
              <VoiceDictationStrip
                status={voice.status}
                error={dictationError}
                onDismissError={() => setDictationError(null)}
              />
            ) : null}

            {replyChip ? (
              <div className="animate-in fade-in duration-200">
                <span
                  data-testid="reply-chip"
                  // Mirrors the label's interpolated handle for tests, which run
                  // with an empty wuchale catalog (all labels resolve to "").
                  data-reply-handle={replyChip.handle}
                  className="text-foreground bg-muted inline-flex w-fit max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                >
                  <Reply className="size-3 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 truncate" title={replyChip.handle}>
                    Will mention {replyChip.handle}
                  </span>
                  <button
                    type="button"
                    data-testid="reply-chip-dismiss"
                    onClick={() => {
                      setDismissedReplyMessageId(replyChip.messageId);
                      // The button unmounts itself — hand focus back to the
                      // input instead of stranding it on <body>.
                      mentionInputRef.current?.focus();
                    }}
                    className="hover:bg-muted-foreground/20 focus-visible:ring-ring ml-0.5 shrink-0 rounded-full p-0.5 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    aria-label={`Don't mention ${replyChip.handle}`}
                  >
                    <X className="size-3" />
                  </button>
                </span>
              </div>
            ) : null}

            <Popover
              open={slashOpen}
              onOpenChange={(open) => {
                if (!open) dismissSlash();
              }}
            >
              <PopoverAnchor asChild>
                <Mention
                  key={inputResetKey}
                  className="w-full"
                  inputValue={newMessage}
                  onInputValueChange={(value) => {
                    if (sendError) {
                      onResetSendError();
                    }
                    if (dictationError === "no_speech") {
                      setDictationError(null);
                    }
                    handleSlashInputChange(value);
                  }}
                  onValueChange={(values) => {
                    mentionedValuesRef.current = values;
                  }}
                  trigger="@"
                >
                  <MentionTextarea
                    ref={mentionInputRef}
                    rows={2}
                    // First-mount seed of the uncontrolled textarea with the ?draft= message.
                    // The initialMessage effect near the top only handles a draft that changes
                    // later: behind the attachments spinner it finds no textarea on mount.
                    defaultValue={isFirstComposerMount ? initialMessage : undefined}
                    className="bg-background rounded-xl text-sm"
                    placeholder="Type a message..."
                    disabled={isComposerBlocked}
                    // CR-202: the transcript rewrites the field while dictating;
                    // typing then would be overwritten.
                    readOnly={isDictating}
                    autoFocus
                    onKeyDown={handleComposerKeyDown}
                  />
                  <MentionMembersContent
                    containerId={roomId}
                    projectContainerId={projectContainerId}
                    membersContainerId={mentionMembersContainerId}
                    excludeUserId={profile.user_id}
                    membersLabel="Chat members"
                  />
                </Mention>
              </PopoverAnchor>
              <PopoverContent
                side="top"
                align="start"
                sideOffset={8}
                className="max-h-80 w-96 overflow-y-auto p-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <SlashCommandContent
                  skills={filteredSkills}
                  selectedIndex={slashSelectedIndex}
                  onSelect={handleSkillSelect}
                />
              </PopoverContent>
            </Popover>
          </div>

          {voice.isSupported ? (
            <VoiceDictationLanguagePicker
              value={dictationLanguage.choice}
              disabled={isDictating}
              onChange={dictationLanguage.setChoice}
            />
          ) : null}
          {voice.isSupported ? (
            <VoiceDictationButton
              status={voice.status}
              disabled={isComposerBlocked}
              onStart={handleStartDictation}
              onStop={() => void voice.stop()}
            />
          ) : null}

          {/* CR-171/CR-172: the send slot is ONE conditional element — Stop while an AI
              reply is in progress (Enter still sends, Q1), else "Send now" while
              dictating (CR-202), else Send once there is a draft. */}
          {isAiTurnActive && onStopAiTurns ? (
            <Button
              variant="secondary"
              className="animate-in fade-in aspect-square shrink-0 rounded-full duration-300"
              size="icon-lg"
              type="button"
              data-testid="stop-ai-turn"
              aria-label={isStoppingAiTurn ? "Stopping the response" : "Stop generating"}
              title="Stop generating"
              disabled={isStoppingAiTurn}
              onClick={onStopAiTurns}
            >
              {isStoppingAiTurn ? (
                <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Square className="size-3.5 fill-current" />
              )}
            </Button>
          ) : (
            (isDictating || !isMessageEmpty) && (
              // CR-202: while dictating this is "Send now": stop, then send the transcript.
              <Button
                variant="secondary"
                className="animate-in fade-in slide-in-from-right-4 aspect-square shrink-0 rounded-full duration-300"
                size="icon-lg"
                type={isDictating ? "button" : "submit"}
                data-testid={isDictating ? "voice-send" : undefined}
                aria-label={isDictating ? "Stop dictation and send" : undefined}
                onClick={isDictating ? () => void handleSendDictation() : undefined}
                disabled={isComposerBlocked || voice.status === "stopping"}
              >
                {isSending ? (
                  <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Send className="size-4" />
                )}
              </Button>
            )
          )}
        </div>
      </form>
    </div>
  );
};
